import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faLock } from '@fortawesome/free-solid-svg-icons';
import FalconCloseButton from 'components/common/FalconCloseButton';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const QUICK_REASONS = [
  'Problema resuelto satisfactoriamente',
  'Duda o consulta aclarada',
  'Sin respuesta del cliente',
  'Se brindó asistencia y configuración remota'
];

const ModalCloseTicket = ({ show, onClose, ticket, onTicketClosed }) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token, idUsuario } = useToken();
  const { showErrorToast, showSuccessToast } = useToast();
  const [motivo, setMotivo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const hasAssignedAgent = Boolean(ticket?.idUsuarioAsignado && Number(ticket.idUsuarioAsignado) > 0);

  useEffect(() => {
    if (show) {
      setMotivo('');
      setErrorMsg('');
      setIsSubmitting(false);
    }
  }, [show]);

  if (!show) return null;

  const handleConfirmClose = async () => {
    if (!hasAssignedAgent) {
      const msg = 'No se puede cerrar el ticket porque no está asignado a ningún agente.';
      setErrorMsg(msg);
      showErrorToast(msg);
      return;
    }

    const trimmed = motivo.trim();
    if (!trimmed) {
      setErrorMsg('El motivo de cierre es obligatorio.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      const ticketId = ticket?.id || ticket?.idTicket;
      const response = await fetch('/api/SupportEngine/CloseTicket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId),
          idUsuario: Number(idUsuario),
          motivoCerrado: trimmed
        })
      });

      if (response.ok) {
        showSuccessToast(`Ticket #${ticketId} cerrado correctamente`);
        if (onTicketClosed) {
          onTicketClosed();
        }
        onClose();
      } else {
        const err = await response.json().catch(() => ({}));
        const msg = err.message || 'Error al cerrar el ticket';
        setErrorMsg(msg);
        showErrorToast(msg);
      }
    } catch (err) {
      const msg = 'Error de red: ' + err.message;
      setErrorMsg(msg);
      showErrorToast(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="md">
      <Modal.Header className="border-bottom-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2 text-danger fs-0">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>Cerrar Ticket #{ticket?.id || ticket?.idTicket}</span>
        </Modal.Title>
        <FalconCloseButton onClick={onClose} disabled={isSubmitting} />
      </Modal.Header>

      <Modal.Body className="pt-2">
        {!hasAssignedAgent && (
          <div className="alert alert-warning d-flex align-items-center gap-2 py-2 px-3 fs--1 mb-3" role="alert">
            <FontAwesomeIcon icon={faLock} className="flex-shrink-0 text-warning" />
            <div>
              Este ticket no está asignado a ningún agente. Debe asignarse a un agente antes de poder cerrarse.
            </div>
          </div>
        )}

        <p className="text-muted fs--1 mb-3">
          ¿Estás seguro de que deseas cerrar el ticket para{' '}
          <strong className="text-dark">{ticket?.customerName || 'el cliente'}</strong>? Una vez cerrado, quedará registrado en el historial con el motivo especificado.
        </p>

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold text-dark fs--1 mb-1">
            Motivo del cierre <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Escribe el motivo del cierre..."
            value={motivo}
            onChange={e => {
              setMotivo(e.target.value);
              if (errorMsg) setErrorMsg('');
            }}
            disabled={isSubmitting || !hasAssignedAgent}
            autoFocus={hasAssignedAgent}
            className={errorMsg ? 'is-invalid' : ''}
          />
          {errorMsg && <div className="invalid-feedback d-block fs--2 mt-1">{errorMsg}</div>}
        </Form.Group>

        {/* Motivos sugeridos */}
        <div>
          <span className="text-muted d-block mb-1" style={{ fontSize: '0.72rem' }}>
            Motivos sugeridos:
          </span>
          <div className="d-flex flex-wrap gap-1">
            {QUICK_REASONS.map((r, i) => (
              <button
                key={i}
                type="button"
                className="btn btn-sm btn-light border text-secondary px-2 py-0.5 rounded-pill"
                style={{ fontSize: '0.72rem' }}
                onClick={() => {
                  setMotivo(r);
                  if (errorMsg) setErrorMsg('');
                }}
                disabled={isSubmitting || !hasAssignedAgent}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer
        className="px-4 py-2.5 border-top d-flex justify-content-end gap-2"
        style={{
          backgroundColor: isDark ? '#121e2d' : '#ffffff',
          borderColor: isDark ? '#233142' : '#e2e8f0'
        }}
      >
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          className="btn btn-sm px-3 shadow-none fw-semibold"
          style={{
            fontSize: '0.82rem',
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            color: isDark ? '#38bdf8' : '#0284c7',
            border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
            transition: 'all 0.15s ease'
          }}
        >
          Cancelar
        </button>
        <Button
          variant="danger"
          size="sm"
          onClick={handleConfirmClose}
          disabled={isSubmitting || !motivo.trim() || !hasAssignedAgent}
          className="d-flex align-items-center gap-1.5 px-3 py-1 fw-semibold"
          style={{ fontSize: '0.82rem' }}
        >
          {isSubmitting ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
              <span>Cerrando...</span>
            </>
          ) : (
            <>
              {!hasAssignedAgent && <FontAwesomeIcon icon={faLock} />}
              <span>Confirmar Cierre</span>
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ModalCloseTicket.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ticket: PropTypes.object,
  onTicketClosed: PropTypes.func
};

export default ModalCloseTicket;
